import { useState, useMemo, useEffect } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { useLocation, useNavigate } from 'react-router-dom';
import SalesGrid from './salesGrid';
import InvoiceGrid from './invoice/invoiceGrid';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import { getGridDataByModuleKey } from '../../redux/selector';
import { invoiceInputData, salesOrderInputData } from '../../shared/constants';
import { useSelector } from 'react-redux';
import Loader from '../../shared/component/Loader';

export const Sales = () => {
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const location = useLocation();
  const navigate = useNavigate();
  const [isTabActive, setIsTabActive] = useState('salesOrder');
  const gridDataSalesOrder = useSelector(getGridDataByModuleKey('salesOrder'));
  const gridDataInvoice = useSelector(getGridDataByModuleKey('invoice'));
  const [loading, setLoading] = useState(false);

  const isOrders = isTabActive === 'salesOrder';
  const soCount = gridDataSalesOrder?.totalCount;
  const invCount = gridDataInvoice?.totalCount;

  useEffect(() => {
    if (location.pathname.includes('/invoice')) {
      setIsTabActive('invoice');
    } else if (location.pathname.includes('/sales/salesOrder')) {
      setIsTabActive('salesOrder');
    }
  }, [location.pathname]);

  const handleIsTabActive = (tab) => {
    if (tab === 'salesOrder' && location.pathname !== '/sales/salesOrder') {
      navigate('/sales/salesOrder');
    } else if (tab === 'invoice' && location.pathname !== '/sales/invoice') {
      navigate('/sales/invoice');
    }
    setIsTabActive(tab);
  };

  const handleCreateInvoice = () => {
    navigate('/sales/createInvoice');
  };

  const handleCreateSales = () => {
    navigate('/sales/createSalesOrder');
  };

  const exportAllSales = async () => {
    setLoading(true);
    const fetchData = {
      ...salesOrderInputData,
      pageSize: gridDataSalesOrder?.totalCount,
      searchText: gridDataSalesOrder?.inputPayload?.searchText ? gridDataSalesOrder?.inputPayload?.searchText : null,
      filterJson: gridDataSalesOrder?.inputPayload?.filterJson ? gridDataSalesOrder?.inputPayload?.filterJson : null,
    };

    try {
      const response = await apiServices.exportAllSales(fetchData);
      if (response?.data && response.status === 200) {
        setLoading(false);
        if (response?.data.includes('Export initiated.')) {
          toast.warning(response?.data);
          setLoading(false);
        } else {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'SalesOrders.csv');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } else {
        console.error('No data received from API.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error exporting SalesOrders:', error);
      setLoading(false);
    }
  };

  const exportAllInovice = async () => {
    setLoading(true);
    const fetchData = {
      ...invoiceInputData,
      pageSize: gridDataInvoice?.totalCount,
      searchText: gridDataInvoice?.inputPayload?.searchText ? gridDataInvoice?.inputPayload?.searchText : null,
      filterJson: gridDataInvoice?.inputPayload?.filterJson ? gridDataInvoice?.inputPayload?.filterJson : null,
    };

    try {
      const response = await apiServices.exportAllInvoice(fetchData);
      if (response?.data && response.status === 200) {
        setLoading(false);
        if (response?.data.includes('Export initiated.')) {
          toast.warning(response?.data);
          setLoading(false);
        } else {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'Invoices.csv');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } else {
        console.error('No data received from API.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error exporting Invoices:', error);
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="inner-page gos-leads gos-sales" style={containerStyle}>
        <div className="dash-container">
          <div className="tbl_container">
            <div className="row">
              <div className="col-12 col-sm-12 col-md-12">
                {/* GrowthOS header */}
                <div className="main-hd">
                  <div className="gos-lead-head">
                    <div className="pagetitle">Sales</div>
                    <div className="gos-page-sub">
                      {(soCount !== undefined ? Number(soCount).toLocaleString() : '0')} sales orders
                      {' · '}
                      {(invCount !== undefined ? Number(invCount).toLocaleString() : '0')} invoices
                    </div>
                  </div>

                  <div className="third-div gos-lead-actions">
                    <button
                      type="button"
                      className="gos-btn gos-btn-ghost"
                      onClick={isOrders ? exportAllSales : exportAllInovice}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></svg>
                      Export
                    </button>
                    <button
                      type="button"
                      className="gos-btn gos-btn-primary"
                      onClick={isOrders ? handleCreateSales : handleCreateInvoice}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                      {isOrders ? 'Create Sales Order' : 'Create Invoice'}
                    </button>
                  </div>
                </div>

                {/* GrowthOS page-level tab bar — Sales Order / Invoice (styling only; tab logic unchanged) */}
                <div className="gos-camptabs">
                  <button
                    type="button"
                    className={'gos-camptabs-tab' + (isOrders ? ' active' : '')}
                    onClick={() => handleIsTabActive('salesOrder')}
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
                    Sales Order
                    {soCount !== undefined && <span className="count">{Number(soCount).toLocaleString()}</span>}
                  </button>
                  <button
                    type="button"
                    className={'gos-camptabs-tab' + (!isOrders ? ' active' : '')}
                    onClick={() => handleIsTabActive('invoice')}
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" /></svg>
                    Invoice
                    {invCount !== undefined && <span className="count">{Number(invCount).toLocaleString()}</span>}
                  </button>
                </div>

                {isOrders ? <SalesGrid /> : <InvoiceGrid />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sales;
